import { useState, useEffect, useMemo } from "react";
import Portal from "../components/common/Portal";
import DoctorScheduleModal from "../components/DoctorScheduleModal";
import DashboardLayout from "../layouts/DashboardLayout";
import api from "../api";
import { toast } from "react-toastify";
import { jwtDecode } from "jwt-decode";
import { Calendar as BigCalendar, dateFnsLocalizer } from "react-big-calendar";
import { format, parse, startOfWeek, getDay } from "date-fns";
import enUS from "date-fns/locale/en-US";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { CalendarDays, Plus, Pencil, Trash2, X, Stethoscope, ListFilter, ArrowLeft, Ban, Clock, Lightbulb } from "lucide-react";
import { Link } from "react-router-dom";

// Kinds of blocked time (calendar tools). "leave" is the original vacation.
const BLOCK_TYPES = { leave: "Leave", admin: "Admin time", peer_consult: "Peer consult", training: "Training", personal: "Personal" };
const BLOCK_COLORS = {
  leave: ["#fef3c7", "#92400e", "#fcd34d"], admin: ["#e0e7ff", "#3730a3", "#a5b4fc"], peer_consult: ["#dcfce7", "#166534", "#86efac"],
  training: ["#e0f2fe", "#075985", "#7dd3fc"], personal: ["#f3e8ff", "#6b21a8", "#d8b4fe"],
};
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const WEEKDAYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
// True when a time falls inside one of the doctor's shifts for that weekday.
const isWorkingTime = (schedule, d) => {
  const day = schedule?.[WEEKDAYS[d.getDay()]];
  if (!day?.is_working) return false;
  const t = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  return (day.shifts || []).some((sh) => sh.start && sh.end && sh.start <= t && t < sh.end);
};
const hm = (d) => `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;

const locales = { "en-US": enUS };
const localizer = dateFnsLocalizer({ format, parse, startOfWeek, getDay, locales });

function CalendarToolbar(toolbar) {
  return (
    <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
      <span className="text-lg font-bold text-secondary-900">{toolbar.label}</span>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => toolbar.onNavigate("PREV")}
          className="px-3 py-1.5 text-sm font-medium text-secondary-600 border border-secondary-200 rounded-lg hover:bg-secondary-50 transition-colors"
        >
          <span className="mr-1">&#171;</span>Previous
        </button>
        <button
          type="button"
          onClick={() => toolbar.onNavigate("TODAY")}
          className="px-3 py-1.5 text-sm font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
        >
          Today
        </button>
        <button
          type="button"
          onClick={() => toolbar.onNavigate("NEXT")}
          className="px-3 py-1.5 text-sm font-medium text-secondary-600 border border-secondary-200 rounded-lg hover:bg-secondary-50 transition-colors"
        >
          Next<span className="ml-1">&#187;</span>
        </button>
        {(toolbar.views || []).length > 1 && (
          <div className="flex rounded-lg border border-secondary-200 overflow-hidden">
            {toolbar.views.map((v) => (
              <button key={v} type="button" onClick={() => toolbar.onView(v)}
                className={`px-3 py-1.5 text-sm font-medium capitalize ${toolbar.view === v ? "bg-secondary-800 text-white" : "text-secondary-600 hover:bg-secondary-50"}`}>{v}</button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function CalendarPage() {
  const [isAdmin, setIsAdmin] = useState(false);
  const [isBranchAdmin, setIsBranchAdmin] = useState(false);
  const [myBranchId, setMyBranchId] = useState(null);
  // A branch admin can manage holidays too, but only ever scoped to their
  // own branch - matches the backend gate (organization.py's
  // _is_org_or_branch_admin / branch_id enforcement).
  const canManageHolidays = isAdmin || isBranchAdmin;
  const [isDoctorRole, setIsDoctorRole] = useState(false);
  const [loggedInDoctorId, setLoggedInDoctorId] = useState(null);
  // The doctor's own sessions, so blocked time and appointments show together.
  const [myAppts, setMyAppts] = useState([]);
  const [viewDate, setViewDate] = useState(new Date());
  useEffect(() => {
    if (!isDoctorRole || !loggedInDoctorId) return;
    const s = new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1);
    const e = new Date(viewDate.getFullYear(), viewDate.getMonth() + 2, 0);
    api.fetchAppointments({ start_date: ymd(s), end_date: ymd(e) })
      .then((r) => setMyAppts((r.data || []).filter((a) => a.doctor_id === loggedInDoctorId && !a.is_cancelled)))
      .catch(() => setMyAppts([]));
  }, [isDoctorRole, loggedInDoctorId, viewDate]);
  // The doctor's own weekly hours: the "My weekly hours" popup and the
  // shading of working hours in week/day views.
  const [myDoctor, setMyDoctor] = useState(null);
  const [showMyHours, setShowMyHours] = useState(false);
  useEffect(() => {
    if (!isDoctorRole || !loggedInDoctorId) return;
    api.getDoctor(loggedInDoctorId).then((r) => setMyDoctor(r.data)).catch(() => setMyDoctor(null));
  }, [isDoctorRole, loggedInDoctorId]);
  const mySchedule = myDoctor?.weekly_schedule;
  const hasHours = WEEKDAYS.some((d) => mySchedule?.[d]?.is_working && (mySchedule[d].shifts || []).length > 0);
  const slotPropGetter = (date) => (hasHours && !isWorkingTime(mySchedule, date)
    ? { style: { backgroundColor: "#f1f5f9" } } : {});
  const [blockDraft, setBlockDraft] = useState(null); // {start_date, end_date, start_time, end_time, block_type, reason}
  const [conflicts, setConflicts] = useState(null);   // sessions booked inside a new block

  const [myExceptions, setMyExceptions] = useState([]);
  const [myExceptionsLoading, setMyExceptionsLoading] = useState(false);
  const [savingMyException, setSavingMyException] = useState(false);
  const [myExceptionForm, setMyExceptionForm] = useState({
    start_date: "",
    end_date: "",
    start_time: "",
    end_time: "",
    reason: "",
    block_type: "leave",
  });

  const [holidays, setHolidays] = useState([]);
  const [holidaysLoading, setHolidaysLoading] = useState(false);
  const [holidayForm, setHolidayForm] = useState({ date: "", name: "", branch_id: "" });
  const [editingHolidayId, setEditingHolidayId] = useState(null);
  const [branches, setBranches] = useState([]);
  const [savingHoliday, setSavingHoliday] = useState(false);
  const [yearFilter, setYearFilter] = useState(String(new Date().getFullYear()));
  const [showAllHolidays, setShowAllHolidays] = useState(false);

  const [exceptions, setExceptions] = useState([]);
  const [exceptionsLoading, setExceptionsLoading] = useState(false);

  useEffect(() => {
    const storedUser = localStorage.getItem("auth");
    if (storedUser) {
      try {
        const authData = JSON.parse(storedUser);
        if (authData.token) {
          const decoded = jwtDecode(authData.token);
          const role = decoded.sub?.role?.toLowerCase() || authData.role?.toLowerCase();
          setIsAdmin(role === "admin");
          if (role === "branch admin") {
            setIsBranchAdmin(true);
            setMyBranchId(decoded.sub?.branch_id || null);
          }
          if (role === "doctor") {
            setIsDoctorRole(true);
            setLoggedInDoctorId(decoded.sub?.user_id || null);
          }
        }
      } catch (err) {
        console.error("Error decoding token:", err);
      }
    }
    fetchHolidays();
    fetchExceptions();
    fetchBranches();
  }, []);

  useEffect(() => {
    if (loggedInDoctorId) loadMyExceptions();
  }, [loggedInDoctorId]);

  const loadMyExceptions = async () => {
    if (!loggedInDoctorId) return;
    setMyExceptionsLoading(true);
    try {
      const res = await api.fetchDoctorExceptions(loggedInDoctorId);
      setMyExceptions(res.data || []);
    } catch (err) {
      console.error("Failed to fetch my unavailability:", err);
    } finally {
      setMyExceptionsLoading(false);
    }
  };

  // Drag across times (week/day view) to block them, or pick days in the
  // month view to mark them off. Saving lists any sessions already booked.
  const onSelectSlot = (slot) => {
    if (!isDoctorRole || !loggedInDoctorId) return;
    const start = slot.start;
    const end = slot.end;
    const wholeDays = slot.slots?.length && hm(start) === "00:00" && hm(end) === "00:00";
    if (wholeDays) {
      const last = new Date(end); last.setDate(last.getDate() - 1);
      setBlockDraft({ start_date: ymd(start), end_date: ymd(last < start ? start : last), start_time: "", end_time: "", block_type: "leave", reason: "" });
    } else {
      setBlockDraft({ start_date: ymd(start), end_date: ymd(start), start_time: hm(start), end_time: hm(end), block_type: "admin", reason: "" });
    }
  };

  const saveBlock = async (draft) => {
    const res = await api.createDoctorException(loggedInDoctorId, {
      start_date: draft.start_date, end_date: draft.end_date,
      start_time: draft.start_time || null, end_time: draft.end_time || null,
      reason: draft.reason || null, block_type: draft.block_type || "leave",
    });
    await loadMyExceptions();
    await fetchExceptions();
    if (res.data?.conflicts?.length) setConflicts(res.data.conflicts);
    else toast.success("Time blocked - patients can't book it now.");
  };

  const handleAddMyException = async () => {
    if (!myExceptionForm.start_date || !myExceptionForm.end_date) {
      toast.error("Please provide both a start date and an end date.");
      return;
    }
    if (myExceptionForm.end_date < myExceptionForm.start_date) {
      toast.error("End date cannot be before start date.");
      return;
    }

    setSavingMyException(true);
    try {
      await saveBlock(myExceptionForm);
      setMyExceptionForm({ start_date: "", end_date: "", start_time: "", end_time: "", reason: "", block_type: "leave" });
    } catch (err) {
      console.error("Failed to add unavailability:", err);
      toast.error(err.response?.data?.error || "Failed to add unavailability record.");
    } finally {
      setSavingMyException(false);
    }
  };

  const handleDeleteMyException = async (exceptionId) => {
    if (!window.confirm("Remove this unavailability record?")) return;
    try {
      await api.deleteDoctorException(loggedInDoctorId, exceptionId);
      setMyExceptions((prev) => prev.filter((e) => e.id !== exceptionId));
      await fetchExceptions();
    } catch (err) {
      console.error("Failed to delete unavailability:", err);
      toast.error(err.response?.data?.error || "Failed to delete unavailability record.");
    }
  };

  const fetchHolidays = async () => {
    setHolidaysLoading(true);
    try {
      const res = await api.fetchOrgHolidays();
      setHolidays(res.data || []);
    } catch (err) {
      console.error("Error fetching holidays:", err);
      toast.error("Failed to load holiday list.");
    } finally {
      setHolidaysLoading(false);
    }
  };

  const fetchBranches = async () => {
    try {
      const res = await api.fetchOrgBranches();
      setBranches(res.data || []);
    } catch (err) {
      console.error("Error fetching branches:", err);
    }
  };

  const fetchExceptions = async () => {
    setExceptionsLoading(true);
    try {
      const res = await api.fetchAllDoctorExceptions();
      setExceptions(res.data || []);
    } catch (err) {
      console.error("Error fetching doctor unavailability:", err);
      toast.error("Failed to load doctor unavailability.");
    } finally {
      setExceptionsLoading(false);
    }
  };

  const handleEditHoliday = (holiday) => {
    setEditingHolidayId(holiday.id);
    setHolidayForm({ date: holiday.date, name: holiday.name, branch_id: holiday.branch_id || "" });
  };

  const handleCancelHolidayEdit = () => {
    setEditingHolidayId(null);
    setHolidayForm({ date: "", name: "", branch_id: "" });
  };

  const handleSaveHoliday = async () => {
    if (!canManageHolidays) {
      toast.error("Only an organization or branch admin can manage holidays.");
      return;
    }
    if (!holidayForm.date || !holidayForm.name.trim()) {
      toast.error("Please provide both a date and a name for the holiday.");
      return;
    }

    // A branch admin can only ever create/edit a holiday scoped to their own
    // branch - the backend enforces this too, but lock it here as well so
    // the form doesn't show a choice that would just get overridden.
    const payload = isBranchAdmin && !isAdmin
      ? { ...holidayForm, branch_id: myBranchId }
      : holidayForm;

    setSavingHoliday(true);
    try {
      if (editingHolidayId) {
        await api.updateOrgHoliday(editingHolidayId, payload);
        toast.success("Holiday updated successfully!");
      } else {
        await api.createOrgHoliday(payload);
        toast.success("Holiday added successfully!");
      }
      handleCancelHolidayEdit();
      await fetchHolidays();
    } catch (err) {
      console.error("Error saving holiday:", err);
      toast.error(err.response?.data?.error || "Failed to save holiday.");
    } finally {
      setSavingHoliday(false);
    }
  };

  const handleDeleteHoliday = async (id) => {
    if (!canManageHolidays) {
      toast.error("Only an organization or branch admin can manage holidays.");
      return;
    }
    if (!window.confirm("Delete this holiday?")) return;

    try {
      await api.deleteOrgHoliday(id);
      toast.success("Holiday deleted.");
      if (editingHolidayId === id) handleCancelHolidayEdit();
      await fetchHolidays();
    } catch (err) {
      console.error("Error deleting holiday:", err);
      toast.error(err.response?.data?.error || "Failed to delete holiday.");
    }
  };

  const availableYears = useMemo(() => {
    const years = new Set(holidays.map((h) => h.date?.slice(0, 4)).filter(Boolean));
    years.add(String(new Date().getFullYear()));
    return Array.from(years).sort();
  }, [holidays]);

  // "Current month" boundaries, computed once per mount (ISO date strings compare lexicographically).
  const currentMonthRange = useMemo(() => {
    const now = new Date();
    const monthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    return {
      key: monthKey,
      start: `${monthKey}-01`,
      end: `${monthKey}-${String(lastDay).padStart(2, "0")}`,
    };
  }, []);

  const currentMonthHolidays = useMemo(
    () => holidays.filter((h) => h.date?.slice(0, 7) === currentMonthRange.key),
    [holidays, currentMonthRange]
  );

  const filteredHolidays = useMemo(() => {
    if (!showAllHolidays) return currentMonthHolidays;
    if (yearFilter === "all") return holidays;
    return holidays.filter((h) => h.date?.startsWith(yearFilter));
  }, [showAllHolidays, currentMonthHolidays, holidays, yearFilter]);

  const currentMonthExceptions = useMemo(
    () => exceptions.filter((e) => e.start_date <= currentMonthRange.end && e.end_date >= currentMonthRange.start),
    [exceptions, currentMonthRange]
  );

  const calendarEvents = useMemo(() => {
    const holidayEvents = holidays.map((h) => ({
      id: `holiday-${h.id}`,
      title: `🎉 ${h.name}`,
      start: new Date(`${h.date}T00:00:00`),
      end: new Date(`${h.date}T00:00:00`),
      allDay: true,
      type: "holiday",
    }));

    const exceptionEvents = exceptions.flatMap((e) => {
      const kind = e.block_type || "leave";
      const label = `${e.doctor_name}: ${kind === "leave" ? "unavailable" : BLOCK_TYPES[kind]}${e.reason ? ` (${e.reason})` : ""}`;
      if (e.start_time && e.end_time) {
        // A part-day block repeats on each day of its range.
        const out = [];
        for (let d = new Date(`${e.start_date}T00:00:00`); ymd(d) <= e.end_date; d.setDate(d.getDate() + 1)) {
          out.push({ id: `exception-${e.id}-${ymd(d)}`, title: label, kind, type: "exception",
            start: new Date(`${ymd(d)}T${e.start_time.slice(0, 5)}:00`), end: new Date(`${ymd(d)}T${e.end_time.slice(0, 5)}:00`) });
        }
        return out;
      }
      // react-big-calendar treats an all-day event's `end` as exclusive, so a
      // multi-day range needs +1 day here to visually include the last day.
      const endExclusive = new Date(`${e.end_date}T00:00:00`);
      endExclusive.setDate(endExclusive.getDate() + 1);
      return [{ id: `exception-${e.id}`, title: label, kind, start: new Date(`${e.start_date}T00:00:00`), end: endExclusive, allDay: true, type: "exception" }];
    });

    const apptEvents = myAppts.filter((a) => a.date && a.time).map((a) => {
      const start = new Date(`${a.date}T${a.time.slice(0, 5)}:00`);
      return { id: `appt-${a.id}`, title: `${a.time.slice(0, 5)} ${a.patient_name}${(a.appttype || "").toLowerCase() === "online" ? " (online)" : ""}`,
        start, end: new Date(start.getTime() + (a.duration_minutes || 30) * 60000), type: "appointment", apptId: a.id };
    });
    return [...holidayEvents, ...exceptionEvents, ...apptEvents];
  }, [holidays, exceptions, myAppts]);

  const eventStyleGetter = (event) => {
    const isHoliday = event.type === "holiday";
    const [bg, fg, bd] = isHoliday ? ["#fee2e2", "#991b1b", "#fca5a5"]
      : event.type === "appointment" ? ["#ffffff", "#1e3a8a", "#93c5fd"] : BLOCK_COLORS[event.kind] || BLOCK_COLORS.leave;
    return {
      style: {
        backgroundColor: bg,
        color: fg,
        border: `1px solid ${bd}`,
        borderRadius: "6px",
        fontSize: "0.75rem",
        fontWeight: 600,
        padding: "1px 4px",
      },
    };
  };


  // A doctor's right column holds their own time-off form, so the clinic-wide time-off list
  // stays on the left for them; for everyone else the right column would otherwise be empty.
  const showOwnTimeOff = Boolean(isDoctorRole && loggedInDoctorId);
  const doctorTimeOffCard = (
    <div className="bg-white rounded-xl shadow-sm border border-secondary-200 p-6 space-y-3">
      <div className="flex items-center gap-2 border-b border-secondary-100 pb-3">
        <Stethoscope className="w-5 h-5 text-amber-600" />
        <h3 className="text-base font-bold text-secondary-900">Doctor Time Off (This Month)</h3>
      </div>

      {exceptionsLoading ? (
        <p className="text-sm text-secondary-400 py-4">Loading...</p>
      ) : currentMonthExceptions.length === 0 ? (
        <p className="text-sm text-secondary-400 py-4">No doctor unavailability this month.</p>
      ) : (
        <div className="divide-y divide-secondary-100 max-h-[420px] overflow-y-auto">
          {currentMonthExceptions.map((e) => (
            <div key={e.id} className="py-3">
              <div className="text-sm font-semibold text-secondary-900">{e.doctor_name}</div>
              <div className="text-xs text-secondary-500">
                {e.start_date} → {e.end_date}
                {(e.start_time || e.end_time) && ` (${e.start_time || "00:00"} - ${e.end_time || "23:59"})`}
              </div>
              {e.reason && <div className="text-xs text-secondary-400 mt-0.5">{e.reason}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <DashboardLayout>
      {showMyHours && myDoctor && <DoctorScheduleModal doctor={myDoctor} onClose={() => setShowMyHours(false)} />}
      <div className="mb-5 flex items-start justify-between flex-wrap gap-4">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-primary-100 text-primary-600 flex items-center justify-center shrink-0">
            <CalendarDays className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-secondary-900 leading-tight">Calendar</h1>
            <p className="text-sm text-secondary-500 mt-0.5">
              {isDoctorRole ? "Your sessions, time off and clinic holidays in one place." : "Clinic holidays and doctors' time off at a glance."}
            </p>
          </div>
        </div>
        {isDoctorRole && myDoctor && (
          <button type="button" onClick={() => setShowMyHours(true)}
            className="inline-flex items-center gap-2 rounded-xl border border-secondary-200 bg-white px-4 py-2.5 text-sm font-semibold text-secondary-800 shadow-sm hover:border-primary-300 hover:text-primary-700 hover:shadow transition">
            <Clock className="w-4 h-4 text-primary-600" /> My weekly hours
          </button>
        )}
      </div>

      {isDoctorRole && loggedInDoctorId && (
        <div className="mb-4 flex items-start gap-2 rounded-xl bg-primary-50/60 border border-primary-100 px-4 py-2.5 text-sm text-secondary-700">
          <Lightbulb className="w-4 h-4 text-primary-600 mt-0.5 shrink-0" />
          <span><span className="font-semibold">Need time off?</span> In Month view, select the days. In Week or Day view, drag across the hours you want to block.</span>
        </div>
      )}

      {/* Calendar */}
      <div className="bg-white rounded-2xl shadow-sm border border-secondary-100 mb-6 flex flex-col" style={{ height: "700px" }}>
        <div className="flex items-center gap-2 flex-wrap px-4 pt-3 pb-2 border-b border-secondary-100 text-xs font-medium text-secondary-600">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-secondary-400 mr-1">Key</span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary-50 px-2.5 py-1">
            <span className="w-2.5 h-2.5 rounded-full bg-red-300" /> Holiday
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary-50 px-2.5 py-1">
            <span className="w-2.5 h-2.5 rounded-full bg-amber-300" /> Doctor unavailable
          </span>
          {isDoctorRole && loggedInDoctorId && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary-50 px-2.5 py-1">
              <span className="w-2.5 h-2.5 rounded-full bg-white border-2 border-blue-400" /> My sessions
            </span>
          )}
          {isDoctorRole && hasHours && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary-50 px-2.5 py-1" title="Shown in Week and Day views">
              <span className="w-2.5 h-2.5 rounded-full bg-slate-300" /> Outside my hours
            </span>
          )}
        </div>
        <div className="flex-1 min-h-0 p-4">
        <BigCalendar
          localizer={localizer}
          events={calendarEvents}
          startAccessor="start"
          endAccessor="end"
          style={{ height: "100%" }}
          views={["month", "week", "day"]}
          defaultView="month"
          step={30}
          timeslots={2}
          scrollToTime={new Date(1970, 0, 1, 8, 0)}
          selectable={Boolean(isDoctorRole && loggedInDoctorId)}
          onSelectSlot={onSelectSlot}
          onNavigate={(d) => setViewDate(d)}
          onSelectEvent={(e) => { if (e.type === "appointment") window.location.assign(`/appointment?highlight=${e.apptId}`); }}
          popup
          eventPropGetter={eventStyleGetter}
          slotPropGetter={slotPropGetter}
          tooltipAccessor={(event) => event.title}
          components={{ toolbar: CalendarToolbar }}
        />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Organization Holidays */}
        <div className="space-y-4">
          {canManageHolidays && (
            <div className="bg-white rounded-xl shadow-sm border border-secondary-200 p-6 space-y-4">
              <div className="flex items-center gap-2 border-b border-secondary-100 pb-3">
                <CalendarDays className="w-5 h-5 text-primary-600" />
                <h3 className="text-base font-bold text-secondary-900">
                  {editingHolidayId ? "Edit Holiday" : "Add Holiday"}
                </h3>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-secondary-700 mb-1">
                    Date
                  </label>
                  <input
                    type="date"
                    value={holidayForm.date}
                    onChange={(e) => setHolidayForm((prev) => ({ ...prev, date: e.target.value }))}
                    className="w-full px-3.5 py-2.5 rounded-lg border border-secondary-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm text-secondary-900"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-secondary-700 mb-1">
                    Holiday Name
                  </label>
                  <input
                    type="text"
                    value={holidayForm.name}
                    onChange={(e) => setHolidayForm((prev) => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g. Diwali, Independence Day"
                    className="w-full px-3.5 py-2.5 rounded-lg border border-secondary-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm text-secondary-900"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-secondary-700 mb-1">
                    Branch
                  </label>
                  {isBranchAdmin && !isAdmin ? (
                    <div className="w-full px-3.5 py-2.5 rounded-lg border border-secondary-200 bg-secondary-50 text-sm text-secondary-500">
                      {branches.find((b) => b.id === myBranchId)?.name || "Your branch"}
                      <span className="block text-[11px] normal-case font-normal text-secondary-400">
                        Branch admins can only add holidays for their own branch.
                      </span>
                    </div>
                  ) : (
                    <select
                      value={holidayForm.branch_id}
                      onChange={(e) => setHolidayForm((prev) => ({ ...prev, branch_id: e.target.value }))}
                      className="w-full px-3.5 py-2.5 rounded-lg border border-secondary-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm text-secondary-900"
                    >
                      <option value="">All Branches</option>
                      {branches.map((b) => (
                        <option key={b.id} value={b.id}>{b.name}</option>
                      ))}
                    </select>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleSaveHoliday}
                  disabled={savingHoliday}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 disabled:opacity-60 text-white text-sm font-semibold shadow-sm transition-colors"
                >
                  {editingHolidayId ? <Pencil className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                  {savingHoliday ? "Saving..." : editingHolidayId ? "Update Holiday" : "Add Holiday"}
                </button>
                {editingHolidayId && (
                  <button
                    type="button"
                    onClick={handleCancelHolidayEdit}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-secondary-300 text-secondary-600 hover:bg-secondary-50 text-sm font-semibold transition-colors"
                  >
                    <X className="w-4 h-4" />
                    Cancel
                  </button>
                )}
              </div>
            </div>
          )}

          <div className="bg-white rounded-xl shadow-sm border border-secondary-200 p-6 space-y-3">
            <div className="flex items-center justify-between border-b border-secondary-100 pb-3 gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <CalendarDays className="w-5 h-5 text-primary-600" />
                <h3 className="text-base font-bold text-secondary-900">
                  {showAllHolidays ? "All Holidays" : "This Month's Holidays"}
                </h3>
              </div>
              <div className="flex items-center gap-2">
                {showAllHolidays && (
                  <select
                    value={yearFilter}
                    onChange={(e) => setYearFilter(e.target.value)}
                    className="px-3 py-1.5 rounded-lg border border-secondary-300 text-xs font-semibold text-secondary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="all">All Years</option>
                    {availableYears.map((y) => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                )}
                <button
                  type="button"
                  onClick={() => setShowAllHolidays((prev) => !prev)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-secondary-300 text-xs font-semibold text-secondary-700 hover:bg-secondary-50 transition-colors"
                >
                  {showAllHolidays ? (
                    <>
                      <ArrowLeft className="w-3.5 h-3.5" />
                      This Month
                    </>
                  ) : (
                    <>
                      <ListFilter className="w-3.5 h-3.5" />
                      View All Holidays
                    </>
                  )}
                </button>
              </div>
            </div>

            {holidaysLoading ? (
              <p className="text-sm text-secondary-400 py-4">Loading holidays...</p>
            ) : filteredHolidays.length === 0 ? (
              <p className="text-sm text-secondary-400 py-4">
                {showAllHolidays ? "No holidays found for this filter." : "No holidays this month."}
              </p>
            ) : (
              <div className="divide-y divide-secondary-100 max-h-80 overflow-y-auto">
                {filteredHolidays.map((h) => (
                  <div key={h.id} className="flex items-center justify-between py-3">
                    <div>
                      <div className="text-sm font-semibold text-secondary-900">{h.name}</div>
                      <div className="text-xs text-secondary-500">
                        {new Date(`${h.date}T00:00:00`).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}
                        {" · "}
                        {h.branch_id
                          ? branches.find((b) => b.id === h.branch_id)?.name || "Branch"
                          : "All Branches"}
                      </div>
                    </div>
                    {(isAdmin || (isBranchAdmin && h.branch_id === myBranchId)) && (
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleEditHoliday(h)}
                          className="p-1.5 text-secondary-400 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors"
                          title="Edit holiday"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteHoliday(h.id)}
                          className="p-1.5 text-secondary-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="Delete holiday"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Doctor Time Off sits here for doctors; others get it in the right column (see below). */}
          {showOwnTimeOff && doctorTimeOffCard}
        </div>

        {/* Doctor Unavailability */}
        <div className="space-y-4">
          {!showOwnTimeOff && doctorTimeOffCard}
          {isDoctorRole && loggedInDoctorId && (
            <div className="bg-white rounded-xl shadow-sm border border-secondary-200 p-6 space-y-3">
              <div className="flex items-center gap-2 border-b border-secondary-100 pb-3">
                <Stethoscope className="w-5 h-5 text-amber-600" />
                <h3 className="text-base font-bold text-secondary-900">My Vacation / Unavailability</h3>
              </div>

              <div className="bg-secondary-50 border border-secondary-200 rounded-xl p-4 space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-secondary-700 mb-1">Start Date <span className="text-red-500">*</span></label>
                    <input
                      type="date"
                      value={myExceptionForm.start_date}
                      onChange={(e) => setMyExceptionForm((prev) => ({ ...prev, start_date: e.target.value }))}
                      className="w-full px-3.5 py-2.5 rounded-lg border border-secondary-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm text-secondary-900"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-secondary-700 mb-1">End Date <span className="text-red-500">*</span></label>
                    <input
                      type="date"
                      value={myExceptionForm.end_date}
                      min={myExceptionForm.start_date || undefined}
                      onChange={(e) => setMyExceptionForm((prev) => ({ ...prev, end_date: e.target.value }))}
                      className="w-full px-3.5 py-2.5 rounded-lg border border-secondary-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm text-secondary-900"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-secondary-700 mb-1">
                      Start Time
                    </label>
                    <input
                      type="time"
                      value={myExceptionForm.start_time}
                      onChange={(e) => setMyExceptionForm((prev) => ({ ...prev, start_time: e.target.value }))}
                      className="w-full px-3.5 py-2.5 rounded-lg border border-secondary-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm text-secondary-900"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-secondary-700 mb-1">
                      End Time
                    </label>
                    <input
                      type="time"
                      value={myExceptionForm.end_time}
                      onChange={(e) => setMyExceptionForm((prev) => ({ ...prev, end_time: e.target.value }))}
                      className="w-full px-3.5 py-2.5 rounded-lg border border-secondary-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm text-secondary-900"
                    />
                  </div>
                </div>
                <p className="text-[11px] text-secondary-500">
                  Leave Start/End Time blank for a full-day (or multi-day) leave. Set both to block only part of each day in this date range.
                </p>
                <div>
                  <label htmlFor="block_type" className="block text-xs font-bold uppercase tracking-wider text-secondary-700 mb-1">Type</label>
                  <select id="block_type" value={myExceptionForm.block_type || "leave"} onChange={(e) => setMyExceptionForm((prev) => ({ ...prev, block_type: e.target.value }))}
                    className="w-full px-3.5 py-2.5 rounded-lg border border-secondary-300 text-sm text-secondary-900 bg-white">
                    {Object.entries(BLOCK_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-secondary-700 mb-1">
                    Reason <span className="text-secondary-400 font-normal normal-case">(optional)</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Annual leave, Conference"
                    value={myExceptionForm.reason}
                    onChange={(e) => setMyExceptionForm((prev) => ({ ...prev, reason: e.target.value }))}
                    className="w-full px-3.5 py-2.5 rounded-lg border border-secondary-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm text-secondary-900"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleAddMyException}
                  disabled={savingMyException}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-60 text-white rounded-lg text-sm font-semibold transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  {savingMyException ? "Saving..." : "Add Vacation / Unavailability"}
                </button>
              </div>

              <div className="space-y-2">
                {myExceptionsLoading ? (
                  <p className="text-sm text-secondary-400">Loading...</p>
                ) : myExceptions.length === 0 ? (
                  <p className="text-sm text-secondary-400">No vacation or unavailability recorded.</p>
                ) : (
                  myExceptions.map((exc) => (
                    <div key={exc.id} className="flex items-center justify-between bg-secondary-50 border border-secondary-200 rounded-lg px-4 py-2.5">
                      <div>
                        <div className="text-sm font-semibold text-secondary-900">
                          {exc.start_date} → {exc.end_date}
                          {(exc.start_time || exc.end_time) && (
                            <span className="text-secondary-500 font-normal"> ({exc.start_time || "00:00"} - {exc.end_time || "23:59"})</span>
                          )}
                        </div>
                        <div className="text-xs text-secondary-500">{BLOCK_TYPES[exc.block_type || "leave"]}{exc.reason ? ` · ${exc.reason}` : ""}</div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleDeleteMyException(exc.id)}
                        className="p-1.5 text-secondary-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                        title="Remove"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      {blockDraft && (
        <Portal><div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setBlockDraft(null)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl space-y-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="flex items-center gap-2 text-base font-bold text-secondary-900"><Ban className="w-5 h-5 text-amber-600" /> {blockDraft.start_time ? "Block this time" : "Mark day off"}</h3>
            <p className="text-sm text-secondary-700">
              {blockDraft.start_date === blockDraft.end_date ? new Date(`${blockDraft.start_date}T00:00:00`).toDateString() : `${blockDraft.start_date} to ${blockDraft.end_date}`}
              {blockDraft.start_time ? `, ${blockDraft.start_time} - ${blockDraft.end_time}` : " (whole day)"}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(BLOCK_TYPES).map(([k, v]) => (
                <button key={k} type="button" onClick={() => setBlockDraft((d) => ({ ...d, block_type: k }))}
                  className={`rounded-full border px-3 py-1 text-xs font-medium ${blockDraft.block_type === k ? "border-secondary-800 bg-secondary-800 text-white" : "border-secondary-300 text-secondary-700"}`}>{v}</button>
              ))}
            </div>
            <input value={blockDraft.reason} onChange={(e) => setBlockDraft((d) => ({ ...d, reason: e.target.value }))} maxLength={200}
              placeholder="Note (optional) - only staff see it" aria-label="Note" className="w-full rounded-lg border border-secondary-300 px-3 py-2 text-sm" />
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setBlockDraft(null)} className="rounded-lg border border-secondary-300 px-4 py-2 text-sm">Cancel</button>
              <button type="button" onClick={async () => { try { await saveBlock(blockDraft); setBlockDraft(null); } catch (err) { toast.error(err.response?.data?.error || "Couldn't block that time."); } }}
                className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white">{blockDraft.start_time ? "Block time" : "Mark day off"}</button>
            </div>
          </div>
        </div></Portal>
      )}
      {conflicts && (
        <Portal><div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setConflicts(null)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl space-y-3" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-base font-bold text-secondary-900">Blocked - but {conflicts.length} session{conflicts.length === 1 ? " is" : "s are"} already booked then</h3>
            <p className="text-sm text-secondary-600">New bookings are stopped. These existing sessions still stand - reschedule or cancel them so the patients know.</p>
            <ul className="divide-y divide-secondary-100 rounded-lg border border-secondary-200">
              {conflicts.map((c) => (
                <li key={c.id} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
                  <span>{c.patient_name} · {new Date(`${c.date}T00:00:00`).toLocaleDateString("en-IN", { day: "numeric", month: "short" })} {c.time}</span>
                  <Link to={`/appointment?highlight=${c.id}`} className="text-xs font-semibold text-primary-700 underline">Open</Link>
                </li>
              ))}
            </ul>
            <div className="flex justify-end"><button type="button" onClick={() => setConflicts(null)} className="rounded-lg bg-secondary-800 px-4 py-2 text-sm font-semibold text-white">OK</button></div>
          </div>
        </div></Portal>
      )}
    </DashboardLayout>
  );
}
